/**
 * useAgentNames Hook
 * 
 * This hook provides access to agent name functionality
 * for tasks and sections throughout the application.
 */

import { useState, useEffect, useCallback } from 'react';
import agentService from '../services/agentService';

/**
 * Hook for managing agent names
 * @returns {Object} Agent name functions and state
 */
const useAgentNames = () => {
  const [initialized, setInitialized] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Initialize the agent service
  useEffect(() => {
    const initializeAgentService = async () => {
      if (initialized) return;
      
      setLoading(true);
      try {
        await agentService.initialize();
        setInitialized(true);
        setError(null);
      } catch (err) {
        console.error('Error initializing agent service:', err);
        setError(err.message || 'Failed to initialize agent service');
      } finally {
        setLoading(false);
      }
    };
    
    initializeAgentService();
  }, [initialized]);
  
  /**
   * Get agent for a task
   * @param {Object} task - The task object
   * @returns {Object} Agent information with name and role
   */
  const getTaskAgent = useCallback((task) => {
    return agentService.getTaskAgent(task);
  }, []);
  
  /**
   * Get default agent for a section
   * @param {Object|string} section - The section object or ID
   * @returns {Object} Agent information with name and role
   */
  const getSectionAgent = useCallback((section) => {
    return agentService.getSectionAgent(section);
  }, []);
  
  /**
   * Get all agents for a section
   * @param {Object|string} section - The section object or ID
   * @returns {Object} Map of task keys to agent information
   */
  const getSectionAgents = useCallback((section) => {
    return agentService.getSectionAgents(section);
  }, []);
  
  /**
   * Get agent for the current context
   * @param {Object} context - Context with task and/or section
   * @returns {Object} Agent information with name and role
   */
  const getContextAgent = useCallback((context) => {
    return agentService.getContextAgent(context);
  }, []);
  
  /**
   * Get avatar URL for an agent
   * @param {string} agentName - Agent name
   * @returns {string} Avatar URL
   */
  const getAgentAvatarUrl = useCallback((agentName) => {
    return agentService.getAgentAvatarUrl(agentName);
  }, []);
  
  return {
    initialized,
    loading,
    error,
    getTaskAgent,
    getSectionAgent,
    getSectionAgents,
    getContextAgent,
    getAgentAvatarUrl
  };
};

export default useAgentNames;